import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { socket } from '../socket';
import ReactionPill from './ReactionPill';
import { getOrCreateIdentity, setIdentity as setIdentityStorage, clearIdentity } from '../utils/identity';
import { verifyStoredToken, clearStoredToken } from '../utils/auth';
import { clearSession } from '../utils/session';
import { fetchRoomPlaylists } from '../utils/roomPlaylistsApi';
import { API_URL } from '../utils/api';
import { useSocketRoom } from '../hooks/useSocketRoom';
import { useI18n } from '../i18n/useI18n';
import UpNextBanner from './UpNextBanner';
import QueueItem from './QueueItem';
import SearchSheet from './SearchSheet';
import RoomJoinGate from './RoomJoinGate';
import { getSanitizedYouTubeEmbedUrl, isValidYouTubeVideoId, getSafeAvatarUrl } from '../utils/validation';
import { isDebugEnabled } from '../utils/debug';
import type { ArtistSummary, DisplayQueueItem, Moment, VideoResult } from '../types';
import {
  countSongsBeforeIndex,
  isMomentItem,
  isSongItem,
} from '../utils/queue';
import KaraokrLogo from './KaraokrLogo';
import RoomPlaylistsModal from './RoomPlaylistsModal';

export default function RoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(getOrCreateIdentity());

  const [displayName, setDisplayName] = useState(identity.name);
  const [splashVisible, setSplashVisible] = useState(true);

  const [quickSong, setQuickSong] = useState('');
  const [ytResults, setYtResults] = useState<VideoResult[]>([]);
  const [ytFromCache, setYtFromCache] = useState(false);
  const [artists, setArtists] = useState<ArtistSummary[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [artistSongs, setArtistSongs] = useState<VideoResult[] | null>(null);
  const [isLoadingArtistSongs, setIsLoadingArtistSongs] = useState(false);
  const artistsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [forceUsedQuery, setForceUsedQuery] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [ytError, setYtError] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<VideoResult | null>(null);
  const [showSearchSheet, setShowSearchSheet] = useState(false);
  const [singerName, setSingerName] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [favorites, setFavorites] = useState<VideoResult[]>([]);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hostDropdownOpen, setHostDropdownOpen] = useState(false);
  const hostDropdownRef = useRef<HTMLDivElement>(null);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsMax, setSettingsMax] = useState(5);
  const [settingsCountdown, setSettingsCountdown] = useState(30);
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsStagePassword, setSettingsStagePassword] = useState('');
  const [settingsPasswordVisible, setSettingsPasswordVisible] = useState(false);
  const [settingsStagePasswordVisible, setSettingsStagePasswordVisible] = useState(false);
  const [settingsHideAvatars, setSettingsHideAvatars] = useState(false);
  const [playlistsOpen, setPlaylistsOpen] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [confirmEndRoomOpen, setConfirmEndRoomOpen] = useState(false);

  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [bigPicturePaused, setBigPicturePaused] = useState(false);
  const [countdownPaused, setCountdownPaused] = useState(false);
  const [bigPictureQRShown, setBigPictureQRShown] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [stageVideoRemaining, setStageVideoRemaining] = useState<number | null>(null);
  const [stageVideoDuration, setStageVideoDuration] = useState<number | null>(null);
  const [momentRemaining, setMomentRemaining] = useState<number | null>(null);

  const [isAddingVideo, setIsAddingVideo] = useState(false);

  type DraggedItem =
    | { kind: 'song'; id: string }
    | { kind: 'moment'; id: string }
    | { kind: 'slot'; slotId: string };
  const [draggedItem, setDraggedItem] = useState<DraggedItem | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);

  const {
    queue,
    isHost,
    isQueueOpen,
    maxMessagesPerUser,
    countdownSeconds,
    isProtected,
    isStageProtected,
    joinStatus,
    joinError,
    roomJoinedReceived,
    showUpNextBanner,
    setShowUpNextBanner,
    doJoin,
    toggleQueue,
    updateRoomSettings,
    leave,
    roomEnded,
    endRoom,
    swapNowPlaying,
    reorder,
    nowPlayingId,
    activeMoment,
    activeMomentEnd,
    momentTemplates,
    showFullQueue,
    endMoment,
    listMoments,
    addMoment,
    removeMoment,
    reorderMoments,
    updateShowFullQueue,
    clients,
    blockedUsers,
    kickUser,
    blockUser,
    unblockUser,
    displayQueue: displayQueueFromHook,
    bigPictureViewers,
    hideAvatars,
    stageLive,
    bpQRShown,
    bpLeaderboardShown,
    bpCountdownPaused,
    bpVideoPaused,
    goLive,
  } = useSocketRoom(roomId, identity, displayName);

  const [hostActionBusy, setHostActionBusy] = useState(false);
  const isStageOnline = bigPictureViewers > 0;
  // Erro do último add (limite do cantor, fila fechada, timeout): banner até
  // ser dispensado. Antes falhava em silêncio e o host não percebia (evento
  // 12/10: adds rejeitados sem feedback visível).
  const [addError, setAddError] = useState<string | null>(null);
  // Add pendente de confirmação: clientMessageId idempotente (retry reutiliza
  // o mesmo id; o Queue Service deduplica).
  const pendingAddRef = useRef<{
    payload: Record<string, unknown>;
    attempts: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);

  // Refs frescos para o guard de bigpicture_video_ended: o efeito de
  // subscrições tem deps [isHost], logo o handler só vê valores via refs.
  const nowPlayingIdRef = useRef(nowPlayingId);
  useEffect(() => {
    nowPlayingIdRef.current = nowPlayingId;
  }, [nowPlayingId]);
  const lastVideoEndedIdRef = useRef<string | null>(null);

  // Host controls refletem a realidade após reloads: estas flags vêm no state
  // (persistidas no gateway) em vez de só por broadcast — sem isto, os botões
  // regressavam ao default após um refresh (ex.: "Pause" quando o countdown
  // estava realmente pausado).
  useEffect(() => {
    setBigPictureQRShown(bpQRShown);
  }, [bpQRShown]);
  useEffect(() => {
    setShowLeaderboard(bpLeaderboardShown);
  }, [bpLeaderboardShown]);
  useEffect(() => {
    setCountdownPaused(bpCountdownPaused);
  }, [bpCountdownPaused]);
  useEffect(() => {
    setBigPicturePaused(bpVideoPaused);
  }, [bpVideoPaused]);

  const hostAction = (fn: () => void) => {
    if (hostActionBusy || !isStageOnline) return;
    setHostActionBusy(true);
    fn();
    setTimeout(() => {
      setHostActionBusy(false);
    }, 600);
  };

  const formatRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins > 0) return `${mins}m ${secs.toString().padStart(2, '0')}s`;
    return `${secs}s`;
  };

  // Use the unified display queue from the server (already normalized by the hook).
  const displayQueue = displayQueueFromHook;

  const visibleQueue = useMemo<DisplayQueueItem[]>(() => {
    if (isHost) return displayQueue;
    const nowPlayingItem = displayQueue.find((i) => isSongItem(i) && i.data.id === nowPlayingId);
    const myItems = displayQueue.filter((i) => isSongItem(i) && i.data.userId === identity.id);
    if (showFullQueue) {
      return displayQueue;
    }
    const result: DisplayQueueItem[] = [];
    if (nowPlayingItem) result.push(nowPlayingItem);
    result.push(...myItems);
    return result;
  }, [displayQueue, isHost, identity.id, nowPlayingId, showFullQueue]);

  // Insert a moment at a given display index. The backend position is the
  // number of songs that should appear before this moment.
  const insertMomentAtDisplayIndex = (moment: Moment, displayIndex: number) => {
    const position = countSongsBeforeIndex(displayQueue, displayIndex);
    addMoment({ ...moment, position });
  };

  // Add a moment at the very end of the current display queue.
  // Negative position is a sentinel that tells the backend to append after
  // every song and moment, regardless of where it would otherwise land.
  const insertMomentAtEnd = (moment: Moment) => {
    addMoment({ ...moment, position: -1 });
  };

  // Move a moment one slot up/down in the display queue. Moments reorder via
  // reorder_moments with per-moment positions (number of songs before each
  // one) — the same payload the drag-and-drop path builds — so this works on
  // touch devices where HTML5 drag-and-drop is unavailable.
  const moveMoment = (id: string, dir: -1 | 1) => {
    const idx = displayQueue.findIndex((i) => isMomentItem(i) && i.data.id === id);
    const target = idx + dir;
    if (idx === -1 || target < 0 || target >= displayQueue.length) return;
    const reordered = [...displayQueue];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(target, 0, moved);
    const entries = reordered
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => isMomentItem(item))
      .map(({ item, index }) => ({
        id: (item as { kind: 'moment'; data: Moment }).data.id,
        position: countSongsBeforeIndex(reordered, index),
      }));
    reorderMoments(
      entries.map((e) => e.id),
      entries.map((e) => e.position)
    );
  };

  useEffect(() => {
    verifyStoredToken().then((auth) => {
      if (auth) {
        setDisplayName(auth.user.name);
        setIsAuthenticated(true);
        const nextIdentity = {
          id: auth.user.id,
          name: auth.user.name,
          avatarUrl: auth.user.avatar_url || '',
          email: auth.user.email || undefined,
        };
        setIdentityStorage(nextIdentity);
        setIdentity(nextIdentity);
      }
    });
  }, []);

  useEffect(() => {
    if (!splashVisible) return;
    const t = setTimeout(() => setSplashVisible(false), 2000);
    return () => clearTimeout(t);
  }, [splashVisible]);

  // Host-only page: redirect singers to the singer view as soon as join completes.
  useEffect(() => {
    // Only redirect non-hosts once the server has authoritatively told us we are
    // not the host. Redirecting on the initial false state would bounce the host
    // to the singer page while the join handshake is still in flight.
    if (joinStatus === 'ready' && roomJoinedReceived && !isHost && roomId) {
      navigate(`/singer/${roomId}`, { replace: true });
    }
  }, [joinStatus, roomJoinedReceived, isHost, roomId, navigate]);

  useEffect(() => {
    if (settingsOpen) {
      setSettingsMax(maxMessagesPerUser);
      setSettingsCountdown(countdownSeconds);
      setSettingsPassword('');
      setSettingsStagePassword('');
      setSettingsHideAvatars(hideAvatars);
      setSettingsError(null);
      setSettingsSaved(false);
      setSettingsSaving(false);
    }
  }, [settingsOpen, maxMessagesPerUser, countdownSeconds, isStageProtected, hideAvatars]);

  useEffect(() => {
    if (showSearchSheet && isAuthenticated) {
      socket.emit('get_recommendations');
    }
  }, [showSearchSheet, isAuthenticated]);

  // Fetch room playlists when search opens.
  useEffect(() => {
    if (!showSearchSheet || !roomId) return;
    let cancelled = false;
    fetchRoomPlaylists(roomId)
      .then((data) => {
        if (!cancelled) {
          setPlaylists(
            data.map((p) => ({
              id: p.id,
              name: p.name,
              songs: (p.items || []).map((item) => ({
                videoId: item.video_id,
                title: item.title,
                thumbnail: item.thumbnail || '',
              })),
            }))
          );
        }
      })
      .catch((err) => {
        console.error('[RoomPage] Failed to load room playlists:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [showSearchSheet, roomId]);

  useEffect(() => {
    if (showSearchSheet) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showSearchSheet]);

  // Load moment templates for the host once joined.
  useEffect(() => {
    if (joinStatus === 'ready' && isHost) {
      listMoments();
    }
  }, [joinStatus, isHost, listMoments]);

  // Código de entrada de 4 dígitos (só o host vê; os singers entram por /c/XXXX).
  const [entryCode, setEntryCode] = useState<string | null>(null);
  useEffect(() => {
    if (joinStatus !== 'ready' || !isHost || !roomId || entryCode) return;
    let cancelled = false;
    fetch(`${API_URL}/api/rooms/${encodeURIComponent(roomId)}/metadata`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.entryCode) setEntryCode(data.entryCode);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [joinStatus, isHost, roomId, entryCode]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (hostDropdownRef.current && !hostDropdownRef.current.contains(e.target as Node)) {
        setHostDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const requestNotifyPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      setShowUpNextBanner(false);
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotifyEnabled(permission === 'granted');
    } catch {
      // ignore
    }
  }, []);

  const searchYouTube = useCallback(() => {
    if (!quickSong.trim()) return;
    setIsSearching(true);
    setYtResults([]);
    setYtFromCache(false);
    setYtError(null);
    socket.emit('search_youtube', { query: quickSong.trim() });
  }, [quickSong]);

  const handleForceYouTubeSearch = useCallback(() => {
    const query = quickSong.trim();
    if (!query) return;
    setIsSearching(true);
    setYtResults([]);
    setYtFromCache(false);
    setYtError(null);
    setForceUsedQuery(query);
    socket.emit('search_youtube', { query, force: true });
  }, [quickSong]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!quickSong.trim()) {
      setYtResults([]);
      setYtFromCache(false);
      setYtError(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => searchYouTube(), 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [quickSong, searchYouTube]);

  // Artist row: top artists when the sheet opens, debounced re-search as the
  // user types (same 500ms pattern as the YouTube search above).
  useEffect(() => {
    if (!showSearchSheet) return;
    const query = quickSong.trim();
    if (!query) {
      socket.emit('search_artists', { query: '' });
      return;
    }
    if (artistsDebounceRef.current) clearTimeout(artistsDebounceRef.current);
    artistsDebounceRef.current = setTimeout(() => {
      socket.emit('search_artists', { query });
    }, 500);
    return () => {
      if (artistsDebounceRef.current) clearTimeout(artistsDebounceRef.current);
    };
  }, [showSearchSheet, quickSong]);

  const handleSelectArtist = useCallback((artist: string) => {
    setSelectedArtist(artist);
    setArtistSongs(null);
    setIsLoadingArtistSongs(true);
    socket.emit('get_artist_songs', { artist });
  }, []);

  const handleClearArtist = useCallback(() => {
    setSelectedArtist(null);
    setArtistSongs(null);
  }, []);

  const failPendingAdd = useCallback((message: string) => {
    const pending = pendingAddRef.current;
    if (pending?.timer) clearTimeout(pending.timer);
    pendingAddRef.current = null;
    setIsAddingVideo(false);
    setAddError(message);
  }, []);

  const ADD_TIMEOUT_MS = 7000;
  const handleAddTimeout = useCallback(() => {
    const pending = pendingAddRef.current;
    if (!pending) return;
    if (pending.attempts < 1) {
      // Retry com o MESMO clientMessageId: o Queue Service deduplica, pelo
      // que um ack perdido nunca cria uma música duplicada (evento 12/10).
      pending.attempts += 1;
      socket.emit('send_message', pending.payload);
      pending.timer = setTimeout(handleAddTimeout, ADD_TIMEOUT_MS);
    } else {
      failPendingAdd('Could not add the song. Please try again.');
    }
  }, [failPendingAdd]);

  const addVideo = useCallback(
    (video: VideoResult) => {
      if (!roomId || isAddingVideo) return;
      setIsAddingVideo(true);
      const customSinger = isHost ? singerName.trim() : '';
      const latestIdentity = getOrCreateIdentity();
      const userId = customSinger
        ? `guest-${customSinger.toLowerCase().replace(/\s+/g, '-')}`
        : latestIdentity.id;
      const userName = customSinger || displayName.trim() || latestIdentity.name || 'Guest';
      // Um UUID por tentativa de adição: retries reutilizam-no (dedup no
      // servidor); um novo clique gera um UUID novo e permite duplicar
      // deliberadamente.
      const payload = {
        roomId,
        userId,
        userName,
        avatarUrl: latestIdentity.avatarUrl || undefined,
        content: video.title,
        metadata: video,
        clientMessageId: crypto.randomUUID(),
      };
      pendingAddRef.current = { payload, attempts: 0, timer: setTimeout(handleAddTimeout, ADD_TIMEOUT_MS) };
      socket.emit('send_message', payload);
      setYtResults([]);
      setQuickSong('');
      setSingerName('');
      setPreviewVideo(null);
      setShowSearchSheet(false);
      // Keep the button disabled until the server broadcasts the queue update
      // so a double click/tap never sends two messages.
    },
    [roomId, displayName, isHost, singerName, isAddingVideo, handleAddTimeout]
  );

  // Release the add-video lock when the queue is updated by the server, and
  // always release it after a short timeout so the UI never gets stuck.
  // Também resolve o add pendente (o servidor já processou o pedido).
  useEffect(() => {
    if (pendingAddRef.current) {
      if (pendingAddRef.current.timer) clearTimeout(pendingAddRef.current.timer);
      pendingAddRef.current = null;
    }
    if (!isAddingVideo) return;
    const t = setTimeout(() => setIsAddingVideo(false), 3500);
    return () => clearTimeout(t);
  }, [queue, isAddingVideo]);

  // Dispensar o banner de erro automaticamente ao fim de 6s.
  useEffect(() => {
    if (!addError) return;
    const t = setTimeout(() => setAddError(null), 6000);
    return () => clearTimeout(t);
  }, [addError]);

  // Countdown for the currently active moment, synced against the server's end time.
  useEffect(() => {
    if (!activeMoment || !activeMomentEnd) {
      setMomentRemaining(null);
      return;
    }
    const end = new Date(activeMomentEnd).getTime();
    const update = () => {
      const remaining = Math.max(0, Math.ceil((end - Date.now()) / 1000));
      setMomentRemaining(remaining);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [activeMoment, activeMomentEnd]);

  useEffect(() => {
    const onYoutubeResults = (res: VideoResult[] | { results: VideoResult[]; fromCache: boolean }) => {
      if (Array.isArray(res)) {
        setYtResults(res);
        setYtFromCache(false);
        console.log('[YouTubeSearch] Results from YouTube API');
      } else {
        setYtResults(res.results);
        setYtFromCache(res.fromCache);
        console.log(`[YouTubeSearch] Results from ${res.fromCache ? 'Meilisearch cache' : 'YouTube API'}`);
      }
      setIsSearching(false);
      setYtError(null);
    };
    const onYoutubeError = (msg: string) => {
      setIsSearching(false);
      setYtError(msg || t('search.error'));
    };
    const onArtistsResults = (data: { artists?: ArtistSummary[] } | ArtistSummary[]) => {
      setArtists(Array.isArray(data) ? data : data?.artists ?? []);
    };
    const onArtistSongsResults = (data: { results?: VideoResult[] } | VideoResult[]) => {
      setArtistSongs(Array.isArray(data) ? data : data?.results ?? []);
      setIsLoadingArtistSongs(false);
    };
    const onBigPictureVideoEnded = (data: { messageId?: string }) => {
      if (!isHost || !roomId) return;
      // Dois Stages abertos emitem dois video_ended para a mesma música e a
      // fila saltava duas posições. O gateway também deduplica; este guard é a
      // última linha de defesa no cliente do host.
      if (data?.messageId) {
        if (lastVideoEndedIdRef.current === data.messageId) return;
        lastVideoEndedIdRef.current = data.messageId;
        if (nowPlayingIdRef.current && data.messageId !== nowPlayingIdRef.current) return;
      }
      socket.emit('delete_top_message', { roomId });
    };
    const onBigPictureVideoStarted = (data: { messageId?: string }) => {
      if (isHost && roomId && data.messageId) {
        socket.emit('set_now_playing', { roomId, messageId: data.messageId });
      }
    };
    const onRecommendations = (data: { history: any[]; favorites: any[] }) => {
      const mapped = (data.favorites || []).map((f: any) => ({
        videoId: f.video_id as string,
        title: f.title as string,
        thumbnail: f.thumbnail as string,
      }));
      setFavorites(mapped);
    };
    // Erros de add (limite do cantor, fila fechada) chegam com a mensagem
    // real do Queue Service; falha o add pendente em vez de silêncio.
    const onAddError = (msg: string) => {
      if (!msg || !pendingAddRef.current) return;
      const isDuetError =
        msg.includes('duet') || msg.includes('song not found') || msg.includes('singer not available');
      if (isDuetError) return;
      failPendingAdd(msg);
    };
    const onBigPictureStateChanged = (data: { isPaused?: boolean }) => {
      setBigPicturePaused(!!data.isPaused);
    };
    const onBigPictureCountdownStateChanged = (data: { paused?: boolean }) => {
      setCountdownPaused(!!data.paused);
    };
    const onBigPictureTimeUpdate = (data: { currentTime?: number; duration?: number }) => {
      const duration = typeof data.duration === 'number' ? data.duration : null;
      const current = typeof data.currentTime === 'number' ? data.currentTime : null;
      if (duration !== null && current !== null && duration > 0) {
        setStageVideoRemaining(Math.max(0, duration - current));
        setStageVideoDuration(duration);
      }
    };
    const onBigPictureShowQR = () => setBigPictureQRShown(true);
    const onBigPictureHideQR = () => setBigPictureQRShown(false);
    const onBigPictureShowLeaderboard = () => setShowLeaderboard(true);
    const onBigPictureHideLeaderboard = () => setShowLeaderboard(false);
    socket.on('youtube_results', onYoutubeResults);
    socket.on('youtube_error', onYoutubeError);
    socket.on('artists_results', onArtistsResults);
    socket.on('artist_songs_results', onArtistSongsResults);
    socket.on('bigpicture_video_started', onBigPictureVideoStarted);
    socket.on('bigpicture_video_ended', onBigPictureVideoEnded);
    socket.on('bigpicture_state_changed', onBigPictureStateChanged);
    socket.on('bigpicture_countdown_state_changed', onBigPictureCountdownStateChanged);
    socket.on('bigpicture_time_update', onBigPictureTimeUpdate);
    socket.on('bigpicture_show_qr', onBigPictureShowQR);
    socket.on('bigpicture_hide_qr', onBigPictureHideQR);
    socket.on('bigpicture_show_leaderboard', onBigPictureShowLeaderboard);
    socket.on('bigpicture_hide_leaderboard', onBigPictureHideLeaderboard);
    socket.on('recommendations_data', onRecommendations);
    socket.on('queue_error', onAddError);
    return () => {
      socket.off('youtube_results', onYoutubeResults);
      socket.off('youtube_error', onYoutubeError);
      socket.off('artists_results', onArtistsResults);
      socket.off('artist_songs_results', onArtistSongsResults);
      socket.off('bigpicture_video_started', onBigPictureVideoStarted);
      socket.off('bigpicture_video_ended', onBigPictureVideoEnded);
      socket.off('bigpicture_state_changed', onBigPictureStateChanged);
      socket.off('bigpicture_countdown_state_changed', onBigPictureCountdownStateChanged);
      socket.off('bigpicture_time_update', onBigPictureTimeUpdate);
      socket.off('bigpicture_show_qr', onBigPictureShowQR);
      socket.off('bigpicture_hide_qr', onBigPictureHideQR);
      socket.off('bigpicture_show_leaderboard', onBigPictureShowLeaderboard);
      socket.off('bigpicture_hide_leaderboard', onBigPictureHideLeaderboard);
      socket.off('recommendations_data', onRecommendations);
      socket.off('queue_error', onAddError);
    };
  }, [isHost]);

  const handleLeave = () => {
    leave();
    navigate('/console');
  };

  const playNext = () => {
    if (displayQueue.length === 0 || activeMoment) return;

    if (!nowPlayingId) {
      // No song playing yet, but a countdown is running: skip to a short 5s
      // countdown so the next singer is introduced smoothly.
      socket.emit('bigpicture_skip_countdown', { roomId });
      return;
    }

    // Skip the currently playing song. If it is still the first item, use the
    // legacy pop path; otherwise remove it by id so hosts never get stuck when
    // the stage and queue order drift (e.g. after swaps or manual overrides).
    // intentional:true marca ação explícita do host: o gateway permite remover
    // o topo sem evidência de palco (guard de proteção ao próximo cantor).
    const firstSong = displayQueue.find((i) => isSongItem(i));
    if (firstSong && firstSong.data.id === nowPlayingId) {
      socket.emit('delete_top_message', { roomId, intentional: true });
    } else {
      socket.emit('delete_own_message', nowPlayingId);
    }
  };

  const canPlayNext = displayQueue.length > 0 && !activeMoment;

  if (splashVisible && joinStatus === 'ready') {
    return (
      <div className="splash-screen">
        <KaraokrLogo height={72} />
      </div>
    );
  }

  if (joinStatus !== 'ready') {
    return (
      <RoomJoinGate
        roomId={roomId || ''}
        mode="host"
        isJoining={joinStatus === 'joining'}
        error={joinError}
        onJoin={({ name, password, user }) => {
          setDisplayName(name);
          if (user) {
            setIdentity({
              id: user.id,
              name: user.name,
              avatarUrl: user.avatar_url || '',
              email: user.email || undefined,
            });
          }
          setSplashVisible(true);
          doJoin(name, password);
        }}
        onGoToSinger={() => navigate(`/singer/${roomId}`, { replace: true })}
      />
    );
  }

  return (
    <div className="landing-page">
      <div className="room-hero-glow" aria-hidden="true" />
      {addError && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#c0392b',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 8,
            zIndex: 9999,
            maxWidth: '92vw',
            boxShadow: '0 4px 12px rgba(0,0,0,.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 14,
          }}
        >
          <span>{addError}</span>
          <button
            onClick={() => setAddError(null)}
            aria-label="Dismiss"
            style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, padding: 0 }}
          >
            ✕
          </button>
        </div>
      )}
      <div className="landing-content">
        <div className="room-page">
          <div className="room-page-inner">
      {isHost ? (
        <header className="room-header-bar">
          <div className="room-header-brand">
            <KaraokrLogo className="room-header-logo" />
            <div className="room-header-info">
              <div className="room-header-name">{roomId}</div>
              <div className="room-header-role">
                {t('room.host')}
                {entryCode ? ` • ${t('room.entryCode')}: ${entryCode}` : ''}
              </div>
            </div>
          </div>
          <div className="room-header-actions">
            <button
              className={`room-badge ${isQueueOpen ? 'room-badge--open' : 'room-badge--closed'}`}
              onClick={toggleQueue}
            >
              {isQueueOpen ? t('room.queueOpen') : t('room.queueClosed')}
            </button>
            <Link to={`/stage/${roomId}`} target="_blank" rel="noopener noreferrer">
              <button className={`btn-secondary btn-sm stage-btn ${bigPictureViewers > 0 ? 'stage-btn--live' : ''}`}>
                <span className="stage-btn__indicator" aria-hidden="true" />
                {t('stage.title')}
              </button>
            </Link>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setPlaylistsOpen(true)}
              aria-label={t('room.playlists.button')}
              title={t('room.playlists.button')}
            >
              🎵
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => setSettingsOpen(true)}
              aria-label={t('room.settings')}
              title={t('room.settings')}
            >
              ⚙️
            </button>
            <div style={{ position: 'relative' }} ref={hostDropdownRef}>
              <button
                className="room-avatar-btn"
                onClick={() => setHostDropdownOpen((v) => !v)}
                aria-label={t('common.menu')}
              >
                <img
                  src={getSafeAvatarUrl(identity.avatarUrl, displayName || 'Host')}
                  alt=""
                />
              </button>
              {hostDropdownOpen && (
                <div className="room-dropdown">
                  <div className="room-dropdown-header">
                    <div className="room-dropdown-name">{displayName || t('common.guest')}</div>
                    {identity.email && (
                      <div className="room-dropdown-email">{identity.email}</div>
                    )}
                  </div>
                  <div
                    className="room-dropdown-item"
                    onClick={() => {
                      setHostDropdownOpen(false);
                      requestNotifyPermission();
                    }}
                  >
                    <span>{notifyEnabled ? '🔔' : '🔕'}</span>
                    <span>{notifyEnabled ? t('room.notificationsOn') : t('room.notificationsOff')}</span>
                  </div>
                  <div
                    className="room-dropdown-item room-dropdown-item--danger"
                    onClick={() => {
                      setHostDropdownOpen(false);
                      clearStoredToken();
                      clearIdentity();
                      clearSession();
                      navigate('/');
                    }}
                  >
                    <span>↪</span>
                    <span>{t('common.logOut')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
      ) : (
        <header className="room-header-bar">
          <div className="room-header-brand">
            <KaraokrLogo className="room-header-logo" />
            <div className="room-header-info">
              <div className="room-header-name">{roomId}</div>
              <div className="room-header-role">{t('room.singer')}</div>
            </div>
          </div>
          <div className="room-header-actions">
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button
                className="room-avatar-btn"
                onClick={() => setDropdownOpen((v) => !v)}
                aria-label={t('common.menu')}
              >
                <img
                  src={getSafeAvatarUrl(identity.avatarUrl, displayName || 'Guest')}
                  alt=""
                />
              </button>
              {dropdownOpen && (
                <div className="room-dropdown">
                  <div className="room-dropdown-header">
                    <div className="room-dropdown-name">{displayName || t('common.guest')}</div>
                    {identity.email && (
                      <div className="room-dropdown-email">{identity.email}</div>
                    )}
                  </div>
                  <div
                    className="room-dropdown-item"
                    onClick={() => {
                      setDropdownOpen(false);
                      requestNotifyPermission();
                    }}
                  >
                    <span>{notifyEnabled ? '🔔' : '🔕'}</span>
                    <span>{notifyEnabled ? t('room.notificationsOn') : t('room.notificationsOff')}</span>
                  </div>
                  <div className="room-dropdown-item room-dropdown-item--muted">
                    <span>⚙️</span>
                    <span>{t('room.settings')}</span>
                  </div>
                  <div
                    className="room-dropdown-item"
                    onClick={() => {
                      setDropdownOpen(false);
                      handleLeave();
                    }}
                  >
                    <span>🚪</span>
                    <span>{t('room.leaveRoom')}</span>
                  </div>
                  <div
                    className="room-dropdown-item room-dropdown-item--danger"
                    onClick={() => {
                      setDropdownOpen(false);
                      clearStoredToken();
                      clearIdentity();
                      clearSession();
                      navigate('/');
                    }}
                  >
                    <span>↪</span>
                    <span>{t('common.logOut')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {showUpNextBanner && queue[0] && (
        <UpNextBanner song={queue[0].content} onDismiss={() => setShowUpNextBanner(false)} />
      )}

      {roomEnded && (
        <div className="room-ended-overlay">
          <div className="room-modal-card room-modal-card--ended">
            <div className="room-modal-icon">🎤</div>
            <h2 className="room-modal-title">{t('room.endedTitle')}</h2>
            <p className="room-modal-lead">
              {t('room.endedMessage')}
            </p>
            <Link to="/join-room">
              <button className="btn-primary" onClick={handleLeave}>
                {t('room.joinAnotherRoom')}
              </button>
            </Link>
          </div>
        </div>
      )}

      {joinError && <div className="room-error">{joinError}</div>}

      {joinStatus !== 'ready' ? (
        <div className="room-joined-loading">{t('room.joining')}</div>
      ) : (
        <div className="room-layout">
          <div className="room-layout__main">
            {displayQueue.length === 0 ? (
              <div className="room-empty">{t('room.emptyQueue')}</div>
            ) : (
              <div className="queue-list panel-card panel-card--tight">
                {visibleQueue.map((item, idx) => {
                  const isMoment = isMomentItem(item);
                  const msg = isSongItem(item) ? item.data : undefined;
                  const moment = isMoment ? item.data : undefined;
                  const id = isMoment ? item.data.id : item.data.id;
                  return (
                    <QueueItem
                      key={id}
                      msg={msg}
                      moment={moment}
                      idx={idx}
                      queue={displayQueue}
                      isHost={isHost}
                      isOwner={!!msg && msg.userId === identity.id}
                      roomId={roomId}
                      isNowPlaying={!activeMoment && !!msg && msg.id === nowPlayingId}
                      draggable={isHost && msg?.id !== nowPlayingId}
                      isDragging={
                        (draggedItem?.kind === 'song' && draggedItem.id === id) ||
                        (draggedItem?.kind === 'moment' && draggedItem.id === id)
                      }
                      isDragOver={dragOverItemId === id}
                      onDragStart={() => {
                        if (isMoment) {
                          setDraggedItem({ kind: 'moment', id });
                        } else {
                          setDraggedItem({ kind: 'song', id });
                        }
                      }}
                      onDragOver={() => setDragOverItemId(id)}
                      onDrop={() => {
                        const targetIndex = displayQueue.findIndex((i) =>
                          isMomentItem(i) ? i.data.id === id : i.data.id === id
                        );
                        if (targetIndex === -1 || !isHost) {
                          setDraggedItem(null);
                          setDragOverItemId(null);
                          return;
                        }

                        if (draggedItem?.kind === 'slot') {
                          const slot = momentTemplates.find((m) => m.id === draggedItem.slotId);
                          if (slot) {
                            insertMomentAtDisplayIndex(slot, targetIndex + 1);
                          }
                        } else if (draggedItem?.kind === 'moment') {
                          const moving = displayQueue.find((i) => isMomentItem(i) && i.data.id === draggedItem.id);
                          if (moving) {
                            const reorderedDisplay = displayQueue.filter((i) =>
                              isMomentItem(i) ? i.data.id !== draggedItem.id : i.data.id !== draggedItem.id
                            );
                            // Insert after the drop target so dropping *on* an item
                            // places the dragged moment right after it.
                            reorderedDisplay.splice(targetIndex + 1, 0, moving);

                            const momentEntries = reorderedDisplay
                              .map((i, index) => ({ item: i, index }))
                              .filter(({ item }) => isMomentItem(item))
                              .map(({ item, index }) => ({
                                id: (item as { kind: 'moment'; data: Moment }).data.id,
                                position: countSongsBeforeIndex(reorderedDisplay, index),
                              }));

                            reorderMoments(
                              momentEntries.map((m) => m.id),
                              momentEntries.map((m) => m.position)
                            );
                          }
                        } else if (draggedItem?.kind === 'song') {
                          const songIds = displayQueue
                            .filter(isSongItem)
                            .map((i) => i.data.id);
                          const fromIndex = songIds.indexOf(draggedItem.id);
                          const dropItemIsSong = isSongItem(displayQueue[targetIndex]);
                          const dropSongId = dropItemIsSong ? displayQueue[targetIndex].data.id : null;
                          let toIndex = dropSongId ? songIds.indexOf(dropSongId) : -1;
                          if (toIndex === -1) {
                            // Dropped on a moment: place after the last song before the drop target.
                            toIndex = countSongsBeforeIndex(displayQueue, targetIndex);
                          }
                          if (fromIndex !== -1 && toIndex !== -1) {
                            const newOrder = [...songIds];
                            const [removed] = newOrder.splice(fromIndex, 1);
                            newOrder.splice(toIndex, 0, removed);
                            reorder(newOrder);
                          }
                        }

                        setDraggedItem(null);
                        setDragOverItemId(null);
                      }}
                      onDragEnd={() => {
                        setDraggedItem(null);
                        setDragOverItemId(null);
                      }}
                      onRemoveMoment={isMoment ? () => removeMoment(id) : undefined}
                      onMoveMoment={isMoment ? (dir) => moveMoment(id, dir) : undefined}
                    />
                  );
                })}
              </div>
            )}
          </div>
          <aside className="room-layout__side">
            {isHost ? (
              <div className="room-controls-panel">
                <div className="room-controls-title">{t('room.hostControls')}</div>

                <div className={`stage-live-card ${bigPictureViewers > 0 ? 'stage-live-card--on' : 'stage-live-card--standby'}`}>
                  <div className="stage-live-card__header">
                    <span className="stage-live-card__dot" aria-hidden="true" />
                    <span className="stage-live-card__status">{t('stage.title')}</span>
                    {bigPictureViewers > 0 && (
                      <span className="stage-live-card__badge">{t('stage.live')}</span>
                    )}
                  </div>
                  <div className="stage-live-card__body">
                    <div className="stage-live-card__row">
                      <span className="stage-live-card__label">{t('room.countdownLabel')}</span>
                      <span className="stage-live-card__value">{countdownSeconds}s</span>
                    </div>
                    {(() => {
                      const nowPlaying = nowPlayingId ? queue.find((m) => m.id === nowPlayingId) : undefined;
                      if (!nowPlaying || activeMoment) return null;
                      return (
                        <div className="stage-live-card__row">
                          <span className="stage-live-card__label">{t('room.nowLive')}</span>
                          <span className="stage-live-card__value stage-live-card__value--song">
                            {nowPlaying.metadata?.title || nowPlaying.content}
                          </span>
                        </div>
                      );
                    })()}
                    {stageVideoRemaining !== null && !activeMoment && (
                      <div className="stage-live-card__row">
                        <span className="stage-live-card__label">{t('room.timeLeft')}</span>
                        <span className="stage-live-card__value">
                          {formatRemaining(stageVideoRemaining)}
                          {stageVideoDuration !== null && stageVideoDuration > 0 && (
                            <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: '0.85em' }}>
                              / {formatRemaining(stageVideoDuration)}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {activeMoment && (
                      <div className="stage-live-card__row">
                        <span className="stage-live-card__label">{t('room.nowLive')}</span>
                        <span className="stage-live-card__value stage-live-card__value--moment">
                          {activeMoment.title}
                        </span>
                      </div>
                    )}
                    {activeMoment && momentRemaining !== null && (
                      <div className="stage-live-card__row">
                        <span className="stage-live-card__label">{t('room.timeLeft')}</span>
                        <span className="stage-live-card__value">{formatRemaining(momentRemaining)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`room-controls-group room-controls-group--expanded ${!isStageOnline ? 'room-controls-group--offline' : ''}`}>
                  {!stageLive && (
                    <button
                      className="host-control-btn host-control-btn--go-live primary"
                      disabled={!isStageOnline || hostActionBusy}
                      onClick={() => hostAction(goLive)}
                      title={t('room.startEventHint')}
                    >
                      {t('room.startEvent')}
                    </button>
                  )}
                  <button
                    className="host-control-btn primary"
                    disabled={!isStageOnline || hostActionBusy || !canPlayNext || !stageLive}
                    onClick={() => hostAction(playNext)}
                    title={!stageLive ? t('room.startEventHint') : !canPlayNext ? t('room.emptyQueue') : t('room.playNext')}
                  >
                    {t('room.playNext')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || (!nowPlayingId && !activeMoment && displayQueue.length === 0)}
                    onClick={() => hostAction(() => socket.emit('bigpicture_toggle_pause', { roomId }))}
                  >
                    {bigPicturePaused ? t('room.play') : t('room.pause')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy}
                    onClick={() => hostAction(() => {
                      // O gateway faz echo deste evento a todos (incl. remetente),
                      // então o estado bigPictureQRShown é atualizado via socket.
                      socket.emit(bigPictureQRShown ? 'bigpicture_hide_qr' : 'bigpicture_show_qr', { roomId });
                    })}
                  >
                    {bigPictureQRShown ? t('room.hideQR') : t('room.showQR')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || (!!nowPlayingId || !!activeMoment || displayQueue.length === 0)}
                    onClick={() => hostAction(() => socket.emit(countdownPaused ? 'bigpicture_resume_countdown' : 'bigpicture_pause_countdown', { roomId }))}
                  >
                    {countdownPaused ? t('room.resumeCountdown') : t('room.pauseCountdown')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || (!!nowPlayingId || !!activeMoment || displayQueue.length === 0)}
                    onClick={() => hostAction(() => socket.emit('bigpicture_skip_countdown', { roomId }))}
                  >
                    {t('room.skipCountdown')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || (!!nowPlayingId || !!activeMoment || displayQueue.length === 0)}
                    onClick={() => hostAction(() => socket.emit('bigpicture_add_10s', { roomId }))}
                  >
                    {t('room.add10s')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || (!!nowPlayingId || !!activeMoment || displayQueue.length === 0)}
                    onClick={() => hostAction(() => socket.emit('bigpicture_add_30s', { roomId }))}
                  >
                    {t('room.add30s')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || !nowPlayingId}
                    onClick={() => hostAction(() => socket.emit('bigpicture_restart', { roomId }))}
                  >
                    {t('room.restart')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || !nowPlayingId}
                    onClick={() => hostAction(() => socket.emit('bigpicture_mute', { roomId }))}
                  >
                    {t('room.mute')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy}
                    onClick={() => hostAction(() => {
                      // Estado atualizado via echo do gateway (ver botão QR).
                      socket.emit(showLeaderboard ? 'bigpicture_hide_leaderboard' : 'bigpicture_show_leaderboard', { roomId });
                    })}
                  >
                    {showLeaderboard ? t('bigpicture.dev.hideLeaderboard') : t('room.showLeaderboard')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || !nowPlayingId || !!activeMoment}
                    onClick={() => hostAction(() => swapNowPlaying(nowPlayingId || undefined))}
                  >
                    {t('room.stepAside')}
                  </button>
                  <button
                    className="host-control-btn"
                    disabled={!isStageOnline || hostActionBusy || !activeMoment}
                    onClick={() => hostAction(() => {
                      endMoment();
                      if (activeMoment?.type === 'end') {
                        setConfirmEndRoomOpen(true);
                      }
                    })}
                  >
                    {t('room.endMoment')}
                  </button>
                </div>

                {activeMoment && (
                  <div className={`active-moment active-moment--${activeMoment.type}`}>
                    <div>
                      <div className="active-moment__title">{activeMoment.title}</div>
                      {activeMoment.description && (
                        <div className="active-moment__desc">{activeMoment.description}</div>
                      )}
                    </div>
                    <button
                      className="host-control-btn"
                      disabled={!isStageOnline || hostActionBusy}
                      onClick={() => hostAction(() => {
                        endMoment();
                        if (activeMoment.type === 'end') {
                          setConfirmEndRoomOpen(true);
                        }
                      })}
                    >
                      {t('room.endMoment')}
                    </button>
                  </div>
                )}

              </div>
            ) : (
              <div className="room-controls-panel">
                <div className="room-controls-title">{t('room.reactionsTitle')}</div>
                <ReactionPill roomId={roomId} queue={queue} />
              </div>
            )}

            {isHost && (
              <>
                <div className="room-users-panel">
                  <div className="room-users-title">{t('room.connectedSingers')}</div>
                  <div className="room-users-list">
                    {clients.length === 0 ? (
                      <div className="room-empty room-empty--inline">{t('room.noSingers')}</div>
                    ) : (
                      clients.map((user) => {
                        const isBlocked = blockedUsers.includes(user.userId);
                        const isSelf = user.userId === identity.id;
                        return (
                          <div key={user.userId} className="room-user-row">
                            <div className="room-user-info">
                              <img
                                src={getSafeAvatarUrl('', user.userName)}
                                alt=""
                                className="room-user-avatar"
                              />
                              <div>
                                <div className="room-user-name">{user.userName}</div>
                                <div className={`room-user-status ${isBlocked ? 'room-user-status--blocked' : ''}`}>
                                  {isBlocked ? t('room.cannotAddSongs') : t('room.canAddSongs')}
                                </div>
                              </div>
                            </div>
                            {!isSelf && (
                              <div className="room-user-actions">
                                <button
                                  className={`room-user-btn ${isBlocked ? '' : 'room-user-btn--danger'}`}
                                  onClick={() => {
                                    if (isDebugEnabled('room') || isDebugEnabled('all')) {
                                      console.log('[RoomPage] moderation click', { action: isBlocked ? 'unblock' : 'block', userId: user.userId, userName: user.userName });
                                    }
                                    isBlocked ? unblockUser(user.userId) : blockUser(user.userId);
                                  }}
                                  aria-label={isBlocked ? t('common.allow') : t('common.block')}
                                  title={isBlocked ? t('common.allow') : t('common.block')}
                                >
                                  {isBlocked ? (
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                                      <path d="M8 12h8" />
                                    </svg>
                                  ) : (
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="10" />
                                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  className="room-user-btn room-user-btn--danger"
                                  onClick={() => {
                                    if (isDebugEnabled('room') || isDebugEnabled('all')) {
                                      console.log('[RoomPage] kick click', { userId: user.userId, userName: user.userName });
                                    }
                                    kickUser(user.userId);
                                  }}
                                  aria-label={t('room.kick')}
                                  title={t('room.kick')}
                                >
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h5" />
                                    <polyline points="17 16 21 12 17 8" />
                                    <line x1="21" y1="12" x2="9" y2="12" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="room-users-panel">
                  <div className="room-users-title">{t('room.moments')}</div>
                  <div className="room-moments-list">
                    {momentTemplates.map((moment) => (
                      <div
                        key={moment.id}
                        className="room-user-row room-user-row--draggable"
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'copy';
                          e.dataTransfer.setData('text/plain', `moment:${moment.id}`);
                          setDraggedItem({ kind: 'slot', slotId: moment.id });
                        }}
                        onDragEnd={() => setDraggedItem(null)}
                        title={t('room.dragToQueue')}
                      >
                        <div className="room-user-info">
                          <div className="moment-slot-icon">
                            {moment.type === 'end' ? '⏹' : moment.type === 'intro' ? '✨' : '⏸'}
                          </div>
                          <div>
                            <div className="room-user-name">
                              {moment.title}
                              {moment.duration !== undefined && moment.duration > 0 && (
                                <span className="moment-duration-badge">
                                  {formatRemaining(moment.duration)}
                                </span>
                              )}
                            </div>
                            <div className="room-user-status">{moment.description}</div>
                          </div>
                        </div>
                        <div className="room-user-actions">
                          <button
                            className="room-user-btn"
                            onClick={() => insertMomentAtEnd(moment)}
                            aria-label={t('room.addToQueueEnd')}
                            title={t('room.addToQueueEnd')}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </aside>
        </div>
      )}

      {(isQueueOpen || isHost) && (
        <button
          onClick={() => setShowSearchSheet(true)}
          className={`fab-add ${showSearchSheet ? 'fab-add--hidden' : ''}`}
          aria-label={t('room.addSong')}
        >
          +
        </button>
      )}

      {settingsOpen && (
        <div
          className="video-preview-overlay"
          onClick={() => setSettingsOpen(false)}
        >
          <div
            className="room-modal-card room-modal-card--settings"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="video-preview-close"
              onClick={() => setSettingsOpen(false)}
            >
              ✕
            </button>
            <h2 className="room-modal-title room-modal-title--padded">
              {t('room.settingsTitle')}
            </h2>
            <div className="room-modal-body">
              <div className="room-setting-row">
                <span className="room-setting-label">{t('room.maxSongsPerSinger')}</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={settingsMax}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) setSettingsMax(val);
                  }}
                  className="room-setting-input"
                />
              </div>
              <div className="room-setting-row">
                <span className="room-setting-label">{t('room.countdownSeconds')}</span>
                <input
                  type="number"
                  min={5}
                  max={300}
                  value={settingsCountdown}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) setSettingsCountdown(val);
                  }}
                  className="room-setting-input room-setting-input--narrow"
                />
              </div>
              <div className="room-setting-row">
                <span className="room-setting-label">{t('room.roomPassword')}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type={settingsPasswordVisible ? 'text' : 'password'}
                    placeholder={t('room.passwordPlaceholder')}
                    value={settingsPassword}
                    onChange={(e) => setSettingsPassword(e.target.value)}
                    maxLength={60}
                    className="room-setting-input"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => setSettingsPasswordVisible((v) => !v)}
                    aria-label={settingsPasswordVisible ? t('room.hidePassword') : t('room.showPassword')}
                    style={{ padding: '8px 10px', fontSize: '0.8rem' }}
                  >
                    {settingsPasswordVisible ? '🙈' : '👁️'}
                  </button>
                </div>
                {isProtected && settingsPassword === '' && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: 6, marginBottom: 0 }}>
                    {t('room.passwordProtectedHint')}
                  </p>
                )}
              </div>
              <div className="room-setting-row">
                <span className="room-setting-label">{t('room.stagePasswordLabel')}</span>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type={settingsStagePasswordVisible ? 'text' : 'password'}
                    placeholder={t('room.stagePasswordPlaceholder')}
                    value={settingsStagePassword}
                    onChange={(e) => {
                      const val = e.target.value.slice(0, 10);
                      setSettingsStagePassword(val);
                    }}
                    maxLength={10}
                    className="room-setting-input"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={() => setSettingsStagePasswordVisible((v) => !v)}
                    aria-label={settingsStagePasswordVisible ? t('room.hideStagePassword') : t('room.showStagePassword')}
                    style={{ padding: '8px 10px', fontSize: '0.8rem' }}
                  >
                    {settingsStagePasswordVisible ? '🙈' : '👁️'}
                  </button>
                </div>
                {isStageProtected && settingsStagePassword === '' && (
                  <p style={{ fontSize: '0.75rem', color: 'var(--success)', marginTop: 6, marginBottom: 0 }}>
                    {t('room.stageProtectedHint')}
                  </p>
                )}
              </div>
              <div className="room-setting-row room-setting-row--top">
                <span className="room-setting-label">{t('room.queueVisibility')}</span>
                <label className="room-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={showFullQueue}
                    onChange={(e) => updateShowFullQueue(e.target.checked)}
                    className="room-setting-checkbox-input"
                  />
                  <span>{t('room.showFullQueue')}</span>
                </label>
              </div>
              <div className="room-setting-row">
                <span className="room-setting-label">{t('room.hideAvatars')}</span>
                <label className="room-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={settingsHideAvatars}
                    onChange={(e) => setSettingsHideAvatars(e.target.checked)}
                    className="room-setting-checkbox-input"
                  />
                  <span>{t('room.hideAvatarsCheckbox')}</span>
                </label>
              </div>
              <p className="room-setting-hint">{t('room.hideAvatarsHint')}</p>
              <div className="room-modal-actions">
                <button
                  className="btn-secondary btn-flex"
                  onClick={() => setSettingsOpen(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-primary btn-flex"
                  disabled={settingsSaving}
                  onClick={() => {
                    setSettingsSaving(true);
                    setSettingsError(null);
                    setSettingsSaved(false);

                    const max = Math.max(1, Math.min(50, settingsMax));
                    const countdown = Math.max(5, Math.min(300, settingsCountdown));
                    const roomPassword = settingsPassword.trim();
                    const stagePassword = settingsStagePassword.trim();

                    const payload: { maxMessages?: number; countdownSeconds?: number; password?: string; stagePassword?: string; hideAvatars?: boolean } = {};
                    if (max !== maxMessagesPerUser) payload.maxMessages = max;
                    if (countdown !== countdownSeconds) payload.countdownSeconds = countdown;
                    if (settingsHideAvatars !== hideAvatars) payload.hideAvatars = settingsHideAvatars;
                    // Envia password vazia para remover proteção, e stage password
                    // vazia para remover proteção do Stage.
                    if (roomPassword !== '' || isProtected) payload.password = roomPassword;
                    if (stagePassword !== '' || isStageProtected) {
                      if (stagePassword !== '' && stagePassword.length < 4) {
                        setSettingsError(t('room.stagePasswordError'));
                        setSettingsSaving(false);
                        return;
                      }
                      payload.stagePassword = stagePassword;
                    }

                    if (Object.keys(payload).length === 0) {
                      setSettingsSaved(true);
                      setSettingsSaving(false);
                      setTimeout(() => setSettingsSaved(false), 2000);
                      setSettingsOpen(false);
                      return;
                    }

                    updateRoomSettings(payload);
                    setSettingsSaved(true);
                    setSettingsSaving(false);
                    setTimeout(() => setSettingsSaved(false), 2000);
                    setSettingsOpen(false);
                  }}
                >
                  {settingsSaving ? t('common.saving') : t('room.saveChanges')}
                </button>
              </div>
              {settingsError && (
                <p style={{ textAlign: 'center', color: 'var(--danger)', fontSize: '0.875rem', marginTop: 12 }}>
                  {settingsError}
                </p>
              )}
              {settingsSaved && !settingsError && (
                <p style={{ textAlign: 'center', color: 'var(--success)', fontSize: '0.875rem', marginTop: 12 }}>
                  {t('room.settingsSaved')}
                </p>
              )}
              <div className="room-modal-divider">
                <button
                  className="btn-danger btn-block"
                  onClick={() => setConfirmEndRoomOpen(true)}
                >
                  {t('room.endRoom')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {playlistsOpen && roomId && (
        <RoomPlaylistsModal roomId={roomId} onClose={() => setPlaylistsOpen(false)} />
      )}

      {confirmEndRoomOpen && (
        <div
          className="video-preview-overlay"
          onClick={() => setConfirmEndRoomOpen(false)}
        >
          <div
            className="room-modal-card room-modal-card--centered"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="room-modal-title">{t('room.endRoomConfirmTitle')}</h2>
            <p className="room-modal-lead">
              {t('room.endRoomConfirmMessage')}
            </p>
            <div className="room-modal-actions">
              <button
                className="btn-secondary btn-flex"
                onClick={() => setConfirmEndRoomOpen(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-danger btn-flex"
                onClick={() => {
                  setConfirmEndRoomOpen(false);
                  setSettingsOpen(false);
                  endRoom();
                }}
              >
                {t('room.confirmEndRoom')}
              </button>
            </div>
          </div>
        </div>
      )}

      <SearchSheet
        open={showSearchSheet}
        onClose={() => {
          setShowSearchSheet(false);
          setSingerName('');
          setYtFromCache(false);
          setSelectedArtist(null);
          setArtistSongs(null);
        }}
        quickSong={quickSong}
        setQuickSong={setQuickSong}
        ytResults={ytResults}
        isSearching={isSearching}
        ytError={ytError}
        artists={artists}
        selectedArtist={selectedArtist}
        artistSongs={artistSongs}
        isLoadingArtistSongs={isLoadingArtistSongs}
        onSelectArtist={handleSelectArtist}
        onClearArtist={handleClearArtist}
        onPreview={setPreviewVideo}
        onAdd={addVideo}
        isAdding={isAddingVideo}
        singerName={isHost ? singerName : undefined}
        setSingerName={isHost ? setSingerName : undefined}
        isAuthenticated={isAuthenticated}
        favorites={favorites}
        playlists={playlists}
        savedSongs={[]}
        resultsFromCache={ytFromCache}
        onForceYouTubeSearch={handleForceYouTubeSearch}
        forceDisabled={forceUsedQuery === quickSong.trim()}
        onToggleFavorite={(video, isFavorite) => {
          socket.emit('toggle_favorite', {
            videoId: video.videoId,
            title: video.title,
            thumbnail: video.thumbnail,
            isFavorite,
          });
          setFavorites((prev) =>
            isFavorite ? [...prev, video] : prev.filter((f) => f.videoId !== video.videoId)
          );
        }}
      />

      {previewVideo && (
        <div
          className="video-preview-overlay"
          onClick={() => setPreviewVideo(null)}
        >
          <div className="video-preview-card" onClick={(e) => e.stopPropagation()}>
            <button className="video-preview-close" onClick={() => setPreviewVideo(null)}>
              ✕
            </button>
            <div className="video-preview-title">
              {previewVideo.title}
            </div>
            <div className="video-preview-frame">
              {previewVideo && isValidYouTubeVideoId(previewVideo.videoId) && (
                <iframe
                  src={`${getSanitizedYouTubeEmbedUrl(previewVideo.videoId)}?autoplay=1`}
                  className="video-preview-iframe"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  loading="eager"
                  title={previewVideo.title}
                />
              )}
            </div>
            <div className="video-preview-actions">
              <button className="btn-primary" onClick={() => addVideo(previewVideo)}>
                {t('room.addToQueue')}
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
}
